import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useParams, useHistory } from "react-router";
import * as Screens from "src/screens";
import { AgendaItemOverview } from "./AgendaItemOverview";
import { CopyAgendaItemPreview } from "./CopyAgendaItemPreview";
import { MessageType } from "src/components/notifications/Notifier";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { InlineCommunicationTimelineEntry } from "src/screens/assemblies/assembly/communication/InlineCommunicationTimelineEntry";
import { COMMUNICATION_TIMELINE_ENTRY } from "src/stores/CommunicationStore";

interface Params {
    assemblyid?: string;
}

const AgendaItemMainBase = observer(() => {
    const { agendaitemStore, assemblyStore, navStore, authStore } = useContext(RootStoreContext);
    const { assemblyid } = useParams<Params>();
    const history = useHistory();
    const { t } = useTranslation();

    useEffect(() => {
        if (assemblyid) {
            // Update navigation state
            agendaitemStore.updateNavigation(assemblyStore.currentAssemblyTitle);

            // Load the current assembly and handle real estate checks
            agendaitemStore.loadAndCheckAssembly(
                assemblyid,
                (route) => history.push(route),
                (message) => agendaitemStore.rootStore.uiStore.printStatusMessage(message, MessageType.WARNING)
            );

            // Set the current assembly ID in the store
            agendaitemStore.setCurrentAssemblyId(assemblyid);

            // Load agenda item templates if agenda items don't exist
            if (!assemblyStore.agendaItemsExists) {
                agendaitemStore.loadAgendaItemTemplates();
            }
        }
    }, [assemblyid, agendaitemStore, assemblyStore, history]);

    useEffect(() => {
        // set header Agendaitems
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly?.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly?.date || ""
                )}`
            );
        }

        // set backbutton Agendaitems
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [assemblyStore, authStore, navStore, t, assemblyStore.currentAssembly]);

    useEffect(() => {
        if (assemblyStore.currentAssembly?.livemode) {
            history.push(
                selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
        }
    }, [assemblyStore.currentAssembly?.livemode, history, authStore.user?.role, assemblyStore.currentAssembly?.id]);

    if (!assemblyStore.currentAssembly) {
        return <Screens.Loading />;
    }

    return (
        <UI.Container className="pt-4" key={agendaitemStore.viewVersion}>
            {assemblyStore.agendaItemsExists ? (
                <AgendaItemOverview />
            ) : (
                <CopyAgendaItemPreview
                    assemblyid={assemblyid || ""}
                    agendaitemTemplatesCounter={agendaitemStore.agendaItemTemplatesCount}
                    agendaItemTemplates={agendaitemStore.agendaItemTemplates}
                    isAssemblyLoaded={!!assemblyStore.currentAssembly}
                />
            )}
            {assemblyStore.agendaItemsExists ? (
                <InlineCommunicationTimelineEntry
                    assemblyId={assemblyid || assemblyStore.currentAssembly?.id || ""}
                    entryState={COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED}
                />
            ) : undefined}
        </UI.Container>
    );
});

export const AgendaItemMain = Sentry.withProfiler(AgendaItemMainBase);
